import { useState } from 'react';
import { testimonials } from '@/mocks/testimonials';
import SeoHead from '@/components/feature/SeoHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = (fd.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢你的留言，我们会尽快回复！');
      setFormData({ name: '', email: '', message: '' });
      return;
    }
    setFormStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9rb3ocnlsngrm4lm9ug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: fd.get('name') as string,
          email: fd.get('email') as string,
          message: fd.get('message') as string,
        }).toString(),
      });
      const responseText = await res.text();
      if (res.ok) {
        setFormStatus('success');
        setFormMessage('感谢你的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setFormStatus('error');
        setFormMessage(responseText || '提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <SeoHead
        title="关于我们"
        description="火影黄漫导航 - 致力于为火影忍者成人向同人漫画爱好者提供最优质的内容导航与创作指南，汇聚资源导航、绘师推荐与同人文化解析。"
        keywords="火影黄漫,关于我们,火影同人导航,火影忍者同人漫画,同人创作指南"
        canonicalUrl="/about"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于我们 - 火影黄漫导航',
          description: '火影黄漫导航是专注于火影忍者成人向同人漫画的导航与创作指南平台。',
          url: 'https://huoying-doujin.com/about',
        }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6 mb-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-6">
                  关于火影黄漫导航
                </h1>
                <div className="space-y-4 text-foreground-700 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">火影黄漫导航</strong>成立于2024年，是专注于火影忍者（NARUTO）成人向同人漫画领域的综合性导航与创作指南平台。
                  </p>
                  <p>
                    我们的使命是为全球火影同人爱好者提供<strong className="text-foreground-900">最全面、最精准的资源导航服务</strong>，同时为同人绘师提供专业的创作指导与社区支持。无论你是寻找优质同人内容的读者，还是希望提升创作水平的绘师，都能在这里找到所需。
                  </p>
                  <p>
                    平台目前已收录<strong className="text-primary-600">180+篇</strong>原创指南文章，覆盖资源导航、创作技巧、绘师推荐、同人文化、深度解析、社区资讯六大分类。我们的编辑团队由资深同人爱好者组成，持续追踪行业动态，确保内容的新鲜度与专业度。
                  </p>
                </div>
              </div>
              <div className="relative h-72 md:h-80 rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20ninja%20village%20atmospheric%20scene%20with%20warm%20lantern%20light%2C%20traditional%20architecture%2C%20anime%20art%20style%2C%20golden%20hour%20lighting%2C%20peaceful%20mood%2C%20soft%20shadows%2C%20no%20characters&width=800&height=600&seq=about-hero-image&orientation=landscape"
                  alt="火影黄漫导航"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { value: '180+', label: '原创文章', icon: 'ri-article-line' },
                { value: '6', label: '内容分类', icon: 'ri-folder-line' },
                { value: '50K+', label: '月访问量', icon: 'ri-user-line' },
                { value: '99.9%', label: '在线率', icon: 'ri-server-line' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-background-100 rounded-xl p-5 md:p-6 text-center"
                >
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 bg-primary-50 rounded-lg">
                    <i className={`${stat.icon} text-lg text-primary-600`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-sm">
                来自火影同人社区的真实评价
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 md:p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, si) => (
                      <div key={si} className="w-4 h-4 flex items-center justify-center">
                        <i
                          className={`text-sm ${
                            si < t.rating
                              ? 'ri-star-fill text-accent-500'
                              : 'ri-star-line text-foreground-400'
                          }`}
                        ></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div className="flex items-center gap-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                      loading="lazy"
                    />
                    <div>
                      <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm mb-6 leading-relaxed">
                  有任何建议、合作意向或资源推荐？欢迎通过以下方式联系我们。我们会在24小时内回复。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50">
                      <i className="ri-mail-line text-lg text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">邮箱</div>
                      <div className="text-sm font-medium text-foreground-900">contact@huoying-doujin.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50">
                      <i className="ri-twitter-x-line text-lg text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">Twitter/X</div>
                      <div className="text-sm font-medium text-foreground-900">@huoying_doujin</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                {formStatus === 'success' ? (
                  <div className="bg-primary-50 border border-primary-200 rounded-xl p-8 text-center">
                    <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4">
                      <i className="ri-checkbox-circle-line text-4xl text-primary-500"></i>
                    </div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">
                      留言已收到！
                    </h3>
                    <p className="text-foreground-600 text-sm">{formMessage}</p>
                  </div>
                ) : (
                  <form
                    onSubmit={handleSubmit}
                    className="bg-background-100 rounded-xl p-6"
                    data-readdy-form="huoying-contact"
                  >
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          姓名
                        </label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="你的称呼"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          邮箱
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                          className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none h-32"
                          placeholder="请输入你想说的话（最多500字）"
                        />
                        <p className="text-xs text-foreground-500 mt-1 text-right">
                          {formData.message.length}/500
                        </p>
                      </div>
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                        aria-hidden="true"
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        disabled={formStatus === 'loading'}
                        className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-semibold text-sm hover:bg-primary-600 transition-all disabled:opacity-60 whitespace-nowrap"
                      >
                        {formStatus === 'loading' ? '提交中...' : '发送留言'}
                      </button>
                    </div>
                    {formStatus === 'error' && (
                      <p className="text-red-500 text-xs mt-3 text-center">{formMessage}</p>
                    )}
                  </form>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}